import * as THREE from 'three';

const SKIP_NAME = /^(OhtDropAnchor|OhtDrop|.*Anchor)$/i;
/** Expanding huge InstancedMesh batches synchronously freezes the main thread. */
const MAX_INSTANCED_EXPAND = 64;

function cloneMaterial(material: THREE.Material | THREE.Material[]): THREE.Material {
  const mat = Array.isArray(material) ? material[0]! : material;
  return mat.clone();
}

/**
 * Flattens a rendered AMHS kit (meshes + instanced box batches) into a static
 * group suitable for `instancedAssetManager.registerAsset`.
 */
export function flattenObject3DForInstancing(source: THREE.Object3D): THREE.Group {
  const root = new THREE.Group();
  root.name = 'AmhsKitTemplateFlat';

  source.updateMatrixWorld(true);
  const rootInverse = source.matrixWorld.clone().invert();

  const tempMatrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();

  const addMeshFromMatrix = (
    geometry: THREE.BufferGeometry,
    material: THREE.Material | THREE.Material[],
    worldMatrix: THREE.Matrix4,
    castShadow: boolean,
    receiveShadow: boolean,
    name: string
  ) => {
    const relative = worldMatrix.clone().premultiply(rootInverse);
    relative.decompose(position, quaternion, scale);
    const mesh = new THREE.Mesh(geometry.clone(), cloneMaterial(material));
    mesh.name = name;
    mesh.position.copy(position);
    mesh.quaternion.copy(quaternion);
    mesh.scale.copy(scale);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = receiveShadow;
    root.add(mesh);
  };

  source.traverse((child) => {
    if (!child.visible) return;
    if (child.name && SKIP_NAME.test(child.name)) return;

    if (child instanceof THREE.InstancedMesh && child.geometry && child.material) {
      child.updateMatrixWorld(true);
      const expandCount = Math.min(child.count, MAX_INSTANCED_EXPAND);
      for (let i = 0; i < expandCount; i += 1) {
        child.getMatrixAt(i, tempMatrix);
        const world = tempMatrix.clone().premultiply(child.matrixWorld);
        addMeshFromMatrix(
          child.geometry,
          child.material,
          world,
          child.castShadow,
          child.receiveShadow,
          `${child.name || 'InstancedMesh'}_${i}`
        );
      }
      return;
    }

    if (
      child instanceof THREE.Mesh &&
      !(child instanceof THREE.InstancedMesh) &&
      child.geometry &&
      child.material
    ) {
      child.updateMatrixWorld(true);
      addMeshFromMatrix(
        child.geometry,
        child.material,
        child.matrixWorld,
        child.castShadow,
        child.receiveShadow,
        child.name || 'Mesh'
      );
    }
  });

  return root;
}
